import z from "zod";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";

import { sureApiCall } from "../axiosClient/sureApi";
import { zodParseWithLog } from "../apiErrorLogs";

const TransportModeApiSchema = z.object({
    id: z.number(),
    code: z.string(),
    family: z.enum(["land", "sea", "air"]),
    isRefrigerated: z.boolean(),
    name: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    ecobalyseProcessUuid: z.string().nullable().optional(),
});

export type TransportMode = z.infer<typeof TransportModeApiSchema>;

export type TransportModeChoice = {
    id: number;
    code: string;
    family: TransportMode["family"];
    isRefrigerated: boolean;
    label: string;
};

type SearchOptions = {
    family?: TransportMode["family"];
    enabled?: boolean;
};

export const useSearchTransportModes = ({ family, enabled = true }: SearchOptions = {}) => {
    const { i18n } = useTranslation();

    const query = useQuery({
        enabled,
        cacheTime: 0,
        staleTime: 0,
        queryKey: ["searchTransportModes", family ?? null, i18n.language],
        queryFn: async () => {
            return await sureApiCall.get("/api/transport_mode_references", {
                params: {
                    itemsPerPage: 100,
                    ...(family ? { family } : {}),
                },
            });
        },
        select: (request) => {
            const members = request.data["hydra:member"] ?? request.data.member ?? [];
            const data = zodParseWithLog(members, TransportModeApiSchema.array(), "searchTransportModes");

            return data.map(
                (item): TransportModeChoice => ({
                    id: item.id,
                    code: item.code,
                    family: item.family,
                    isRefrigerated: item.isRefrigerated,
                    label: item.name && item.name.trim() !== "" ? item.name : item.code,
                }),
            );
        },
    });

    return {
        transportModes: query.data ?? [],
        ...query,
    };
};
